import { Effect, Schema, SchemaGetter, SchemaIssue } from "effect";
import { AttemptId, CommitId, PrincipalId, WorkspaceId, WorktreeId } from "./development-identifiers.ts";
import { IdentityArrayInput, IdentityObjectInput, IdentityRecordInput } from "./identity-input.ts";
import { DataPropertyDescriptor, OwnPropertyDescriptors } from "./property-inspection.ts";
import { StationPath } from "./station-path.ts";
import { schemaParser } from "./schema-compat.ts";

const NonNegativeInteger = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)))();
const RefName = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/),
  Schema.makeFilter(value => !value.includes("//") && !value.split("/").some(part => part === "." || part === ".."))
))();

export const WorktreeLeaseView = /* @__PURE__ */ (() => Schema.Struct({
  attemptId: AttemptId,
  holderPrincipalId: Schema.optional(PrincipalId),
  fence: NonNegativeInteger,
  expiresAt: NonNegativeInteger,
  acquiredAt: NonNegativeInteger,
  renewedAt: NonNegativeInteger,
  revokedAt: Schema.optional(NonNegativeInteger),
  revokeReason: Schema.optional(Schema.String)
}))();
export type WorktreeLeaseView = typeof WorktreeLeaseView.Type;

const WorktreeMetadata = /* @__PURE__ */ (() => Schema.Struct({
  worktreeId: WorktreeId,
  baseCommitId: CommitId,
  headCommitId: Schema.optional(CommitId),
  branchRef: Schema.optional(RefName),
  version: NonNegativeInteger,
  fence: NonNegativeInteger,
  state: Schema.Literals(["active", "cancelled", "preserved", "tombstoned", "destroyed"]),
  createdByPrincipalId: Schema.optional(PrincipalId),
  createdAt: NonNegativeInteger,
  updatedAt: NonNegativeInteger,
  cleanupState: Schema.Literals(["none", "pending", "retry", "complete"]),
  cleanupAttempts: NonNegativeInteger,
  lease: Schema.optional(WorktreeLeaseView)
}))();

export const WorktreeSummary = /* @__PURE__ */ (() => Schema.Struct({
  workspaceCount: NonNegativeInteger,
  fileCount: NonNegativeInteger,
  paths: Schema.Array(Schema.String).check(Schema.isMaxLength(500)),
  pathsTruncated: Schema.Boolean,
  activeWorkspaceId: Schema.NullOr(WorkspaceId)
}))();
export type WorktreeSummary = typeof WorktreeSummary.Type;

export const WorktreeView = /* @__PURE__ */ (() => Schema.Struct({
  ...WorktreeMetadata.fields,
  summary: Schema.optional(WorktreeSummary),
  bounded: Schema.optional(Schema.Literal(true))
}).check(Schema.makeFilter(value => (value.summary === undefined) === (value.bounded === undefined))))();
export type WorktreeView = typeof WorktreeView.Type;

export class WorktreeResponseError extends Schema.TaggedError<WorktreeResponseError>()(
  "WorktreeResponseError", { message: Schema.String, status: Schema.tag(500) }
) {}

const ManifestSummaryInput = /* @__PURE__ */ (() => Schema.Struct({
  workspaceOrder: Schema.optional(Schema.Array(WorkspaceId)),
  activeWorkspaceId: Schema.optional(WorkspaceId)
}))();
const ownDescriptors = /* @__PURE__ */ (() => Schema.decodeUnknownSync(OwnPropertyDescriptors))();
const descriptor = /* @__PURE__ */ (() => Schema.decodeUnknownSync(Schema.UndefinedOr(DataPropertyDescriptor)))();
const decodeObject = /* @__PURE__ */ (() => Schema.decodeUnknownSync(IdentityObjectInput))();
const decodeMetadata = /* @__PURE__ */ (() => Schema.decodeUnknownSync(WorktreeMetadata))();
const decodeManifest = /* @__PURE__ */ (() => Schema.decodeUnknownSync(ManifestSummaryInput))();
const decodeWorkspaces = /* @__PURE__ */ (() => Schema.decodeUnknownSync(IdentityArrayInput))();
const decodeWorkspaceId = /* @__PURE__ */ (() => Schema.decodeUnknownSync(WorkspaceId))();
const decodeFiles = /* @__PURE__ */ (() => Schema.decodeUnknownSync(IdentityRecordInput))();
const decodePaths = /* @__PURE__ */ (() => Schema.decodeUnknownSync(Schema.Array(StationPath)))();

/** Read only declared data fields; an unrelated getter or source blob stays untouched. */
function ownDataFields(value: unknown, keys: readonly string[]): Record<string, unknown> {
  const properties = ownDescriptors(value);
  const output: Record<string, unknown> = {};
  for (const key of keys) {
    const property = descriptor(properties[key]);
    if (property !== undefined && property.value !== undefined) output[key] = property.value;
  }
  return output;
}

function projectWorktree(value: object, maximumPaths: number): WorktreeView {
  const fields = ownDataFields(value, [...Object.keys(WorktreeMetadata.fields), "manifest"]);
  if (fields.lease !== undefined) fields.lease = ownDataFields(fields.lease, Object.keys(WorktreeLeaseView.fields));
  const metadata = decodeMetadata(fields);
  if (fields.manifest === undefined || fields.manifest === null) return metadata;
  const manifest = decodeManifest(ownDataFields(fields.manifest, Object.keys(ManifestSummaryInput.fields)));
  const paths: string[] = [];
  const workspaces = ownDataFields(value, ["workspaces"]).workspaces;
  for (const workspace of decodeWorkspaces(workspaces ?? [])) {
    const projected = ownDataFields(workspace, ["workspaceId", "files"]);
    const workspaceId = decodeWorkspaceId(projected.workspaceId);
    // The summary validates names without reading any file value, including
    // accessor-backed values or opaque blobs that must never enter this response.
    const files = decodeFiles(projected.files);
    for (const path of decodePaths(Object.keys(files))) paths.push(`${workspaceId}:${path}`);
  }
  paths.sort();
  const listed = paths.slice(0, maximumPaths);
  return {
    ...metadata,
    summary: {
      workspaceCount: manifest.workspaceOrder?.length ?? 0,
      fileCount: paths.length,
      paths: listed,
      pathsTruncated: paths.length > listed.length,
      activeWorkspaceId: manifest.activeWorkspaceId ?? null
    },
    bounded: true
  };
}

/** One-way projection: the bounded view cannot reconstruct repository contents. */
// Inspect the source inside the fallible transform. A union at the source
// performs native array discrimination before our unreadable-object boundary.
const responseSchema = (maximumPaths: 200 | 500) => Schema.Unknown.pipe(Schema.decodeTo(Schema.NullOr(WorktreeView), {
  decode: SchemaGetter.transformOrFail(value => Effect.try({
    try: () => value === null ? null : projectWorktree(decodeObject(value), maximumPaths),
    catch: () => new SchemaIssue.InvalidValue({ message: "Repository returned an invalid worktree." })
  })),
  encode: SchemaGetter.forbidden(() => "A bounded worktree response cannot reconstruct repository contents.")
}));

// Retain the established transport bounds while sharing the projection contract.
export const BoundedWorktreeResponse = /* @__PURE__ */ (() => responseSchema(200))();
export const BoundedHttpWorktreeResponse = /* @__PURE__ */ (() => responseSchema(500))();
const responseError = () => new WorktreeResponseError({ message: "Repository returned an invalid worktree." });
export const boundWorktreeResponse = /* @__PURE__ */ (() => schemaParser(BoundedWorktreeResponse, responseError))();
export const boundHttpWorktreeResponse = /* @__PURE__ */ (() => schemaParser(BoundedHttpWorktreeResponse, responseError))();
